import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  ExportColumnOption,
  ExportDownloadFormats
} from '@eview/core/domain/export/export-download.service';

export type ExportFormatKind = 'excel' | 'pdf';

export interface ExportColumnsModalResult {
  columnKeys: string[];
  formats: ExportDownloadFormats;
}

@Component({
  selector: 'eview-export-columns-modal',
  templateUrl: './export-columns-modal.component.html',
  styleUrls: ['./export-columns-modal.component.scss']
})
export class ExportColumnsModalComponent implements OnInit {
  @Input() columns: ExportColumnOption[] = [];
  @Input() titleKey = 'EXPORT_COLUMNS_TITLE';

  selected = new Set<string>();
  /** Exclusive: Excel or PDF (never both). */
  formatKind: ExportFormatKind = 'excel';
  filterText = '';

  constructor(public activeModal: NgbActiveModal) {}

  ngOnInit(): void {
    this.selectAll();
  }

  get hasFilter(): boolean {
    return !!(this.filterText || '').trim();
  }

  get filtered(): ExportColumnOption[] {
    const q = (this.filterText || '').trim().toLowerCase();
    if (!q) {
      return this.columns || [];
    }
    return (this.columns || []).filter(
      c =>
        (c.label || '').toLowerCase().includes(q) ||
        (c.key || '').toLowerCase().includes(q)
    );
  }

  get metaColumns(): ExportColumnOption[] {
    return this.filtered.filter(c => c.group === 'meta');
  }

  get formColumns(): ExportColumnOption[] {
    return this.filtered.filter(c => c.group === 'form');
  }

  /**
   * Two visual columns: fill left top→bottom first, then right.
   * (Avoids CSS row flow that zigzags 1,2 / 3,4.)
   */
  columnPair(cols: ExportColumnOption[]): {
    left: ExportColumnOption[];
    right: ExportColumnOption[];
  } {
    const list = cols || [];
    const mid = Math.ceil(list.length / 2);
    return {
      left: list.slice(0, mid),
      right: list.slice(mid)
    };
  }

  get selectedCount(): number {
    return this.selected.size;
  }

  get totalCount(): number {
    return (this.columns || []).length;
  }

  get allVisibleSelected(): boolean {
    const list = this.filtered;
    return list.length > 0 && list.every(c => this.selected.has(c.key));
  }

  get canConfirm(): boolean {
    return this.selected.size > 0 && !!this.formatKind;
  }

  isSelected(key: string): boolean {
    return this.selected.has(key);
  }

  groupSelectedCount(cols: ExportColumnOption[]): number {
    return cols.filter(c => this.selected.has(c.key)).length;
  }

  groupAllSelected(cols: ExportColumnOption[]): boolean {
    return cols.length > 0 && cols.every(c => this.selected.has(c.key));
  }

  groupSomeSelected(cols: ExportColumnOption[]): boolean {
    const n = this.groupSelectedCount(cols);
    return n > 0 && n < cols.length;
  }

  toggle(key: string): void {
    if (this.selected.has(key)) {
      this.selected.delete(key);
    } else {
      this.selected.add(key);
    }
  }

  toggleGroup(cols: ExportColumnOption[]): void {
    if (this.groupAllSelected(cols)) {
      cols.forEach(c => this.selected.delete(c.key));
    } else {
      cols.forEach(c => this.selected.add(c.key));
    }
  }

  selectAll(): void {
    (this.columns || []).forEach(c => this.selected.add(c.key));
  }

  selectNone(): void {
    this.selected.clear();
  }

  toggleVisible(): void {
    if (this.allVisibleSelected) {
      this.filtered.forEach(c => this.selected.delete(c.key));
    } else {
      this.filtered.forEach(c => this.selected.add(c.key));
    }
  }

  chooseFormat(kind: ExportFormatKind): void {
    this.formatKind = kind;
  }

  confirm(): void {
    if (!this.canConfirm) {
      return;
    }
    const columnKeys = (this.columns || [])
      .map(c => c.key)
      .filter(k => this.selected.has(k));
    const result: ExportColumnsModalResult = {
      columnKeys,
      formats: {
        excel: this.formatKind === 'excel',
        pdf: this.formatKind === 'pdf'
      }
    };
    this.activeModal.close(result);
  }

  cancel(): void {
    this.activeModal.dismiss();
  }
}
